/**
 * Intake links, web-side: which cards and which elements an anonymous form
 * may carry, and what the API's refusals mean in plain words.
 *
 * The API is the authority (keepr-api utils/shareLinkIntake.js) and refuses
 * every one of these at mint time, re-checks them on every form fetch and
 * every submit, and answers a dead link with the flat 404. What this file is
 * for is showing the truth BEFORE the request: a card that cannot take intake
 * is offered disabled with its reason, and an element that can never be
 * collected is shown disabled beside the reason, rather than quietly left
 * off — "why can I not collect the inspector?" has a real answer, and a
 * manager should not have to press Create to hear it.
 *
 * Every rule below mirrors one in shareLinkIntake.js; the comments name it.
 */

import { formatWait, retryAfterSeconds } from './apiErrors';
import { retryAfterMs } from './retryAfter';
import { columnsOf, isRepeatingGroup, maxEntriesOf } from '../config/repeatingGroups';

/** The link kind the API mints. */
export const INTAKE_KIND = 'intake';

/** The lifetime cap: default and ceiling (DEFAULT_/MAX_MAX_SUBMISSIONS). */
export const DEFAULT_MAX_SUBMISSIONS = 100;
export const MAX_MAX_SUBMISSIONS = 10000;

/** Manager-authored copy shown to strangers (MAX_HEADLINE / MAX_COPY). */
export const MAX_HEADLINE = 200;
export const MAX_COPY = 2000;

/** The most elements one link may collect (MAX_FIELDS). */
export const MAX_FIELDS = 100;

/**
 * The per-LINK day budget (keepr-api utils/rateLimit.js, class `intake`, the
 * `capability` rule). The API's 429 is `rate_limited` for every rule; which
 * budget ran out is told by the `RateLimit-Limit` header it sends (exposed
 * through CORS) — the per-address rules are 20 an hour and 100 a day, so
 * 200 is the link's alone.
 */
export const INTAKE_LINK_DAILY_LIMIT = 200;

// The data types the API's machine write path knows (automations/coercion
// KNOWN_TYPES). An element of any other type cannot be typed safely by a
// stranger, and the API refuses it.
const KNOWN_TYPES = [
	'text-small', 'text-large', 'rich-text', 'choice', 'number', 'decimal',
	'integer', 'boolean', 'date', 'date-time', 'time', 'url', 'phone', 'email',
	'location', 'rating', 'card-lookup', 'measurement', 'currency', 'user',
	'color', 'file', 'confirmations', 'repeating-group',
];

const isSequenceElement = el => Boolean(el?.options?.sequence && typeof el.options.sequence === 'object');

/** A `file` element: a photo or a document (KPR-388 lets a public form collect one). */
export const isFileElement = element => `${element?.dataType || ''}` === 'file';

/**
 * Why this element can never be an INPUT on an anonymous form, or null when
 * it may (neverExposableReason, and for a file the collection's attachments —
 * keepr-api shareLinkIntake inputRefusal). The words are the API's own, so
 * the tooltip here and the 400 a hand-built request would get say the same
 * thing. `attachmentsOn`: the collection's `allowAttachments`; a form takes
 * files only where attachments are on.
 */
export function intakeBlockReason(element, { attachmentsOn = true } = {}) {
	const type = `${element?.dataType || ''}`;
	if (type === 'user') {
		return 'A person field names real accounts — an anonymous form can never collect one.';
	}
	if (type === 'card-lookup') {
		return 'A record lookup would let a visitor browse this collection’s records. Fill it in for them instead.';
	}
	// A file (KPR-388): the visitor uploads it through the form itself, its
	// photos re-saved without location or camera details — but only where the
	// collection takes attachments at all (keepr-api intakeUploads
	// fileFieldRefusal).
	if (type === 'file' && !attachmentsOn) {
		return 'Attachments are switched off in this collection, so a form cannot take files. Turn them on in its settings first.';
	}
	// A "Me too" count (KPR-390; keepr-api utils/shareLinkIntake.js
	// NEVER_INPUT_TYPES, its sentence word for word): a stranger is nobody to
	// count, and a count is never typed — so never an input, and never a
	// prefill either.
	if (type === 'confirmations') {
		return 'This counts the signed-in people who confirmed — nobody types it, so a form cannot collect it.';
	}
	// A repeating group (KPR-689) is asked like any field — the visitor adds
	// its entries — unless it holds a file column (keepr-api formDefinition
	// groupShapeRefusal, word for word). The rest of what can stop a group (a
	// required column the public's rules keep) is the API's to say.
	if (isRepeatingGroup(element)) {
		const file = columnsOf(element).find(column => isFileElement(column));
		if (file) {
			return `“${labelOf(file)}” is a file column, and a public form takes files only as elements of the card, never inside a repeating group. Put the file element on the card beside the group.`;
		}
	}
	if (element?.options?.drivenFrom) {
		return 'This value is calculated by keepr, so there is nothing for a visitor to type.';
	}
	if (isSequenceElement(element)) {
		return 'This number is issued by keepr when the submission arrives, so there is nothing for a visitor to type.';
	}
	if (!KNOWN_TYPES.includes(type)) {
		return 'This kind of field cannot be collected on an anonymous form.';
	}
	return null;
}

/**
 * Why this element can never be PREFILLED, or null (neverPrefillableReason):
 * the input rule, less one type — a record lookup may be filled in by the
 * manager, which is what a prefill is for.
 */
export function prefillBlockReason(element) {
	if (element?.dataType === 'card-lookup') return null;
	// KPR-689: a group's entries are the visitor's to add (neverPrefillableReason).
	if (isRepeatingGroup(element)) return 'A repeating group’s entries are added by the visitor — a form cannot fill them in.';
	// A file value names one record's attachment: a manager cannot fill one in.
	if (isFileElement(element)) return 'A file cannot be filled in for the visitor — a file belongs to one record.';
	return intakeBlockReason(element);
}

/** Required regardless of what the row holds — `options.required` alone. */
export const isAlwaysRequired = element => element?.options?.required === true;

/**
 * Required now, or able to BECOME required (`requiredWhen`). The API refuses
 * a link that leaves either kind unanswered (intake_required_uncovered): a
 * visitor cannot be asked for a field they were never shown, so every
 * element that might be demanded has to be on the form or filled in for them.
 */
export const mustBeCovered = element => isAlwaysRequired(element)
	|| (Array.isArray(element?.options?.requiredWhen) && element.options.requiredWhen.length > 0)
	// A repeating group that needs an entry (KPR-689): only the visitor can add
	// one, so it must be on the form (keepr-api uncoveredRequired).
	|| (isRepeatingGroup(element) && groupLimitsOf(element).min > 0);

// ------------------------------------------------- repeating groups (KPR-689)
//
// A form asks a repeating group like any field, and sets the fewest and most
// entries IT takes (`entryLimits`), only ever narrower than the group's own
// (keepr-api formDefinition resolveEntryLimits — the sentences below are its
// own, so the hint here and a refusal agree).

/** A group's own limits: `{ min, max }` (minEntries, else 0; maxEntries, else 100). */
export function groupLimitsOf(element) {
	const min = element?.options?.minEntries;
	return { min: Number.isInteger(min) && min > 0 ? min : 0, max: maxEntriesOf(element) };
}

const entriesWord = n => (n === 1 ? 'entry' : 'entries');

/**
 * Why `limits` ({ min?, max? }, either blank = the group's) cannot be the
 * form's for group `element`, or null — the API's intake_entry_limits.
 */
export function entryLimitsProblem(element, limits = {}) {
	const own = groupLimitsOf(element);
	const group = labelOf(element);
	const min = Number.isInteger(limits?.min) ? limits.min : null;
	const max = Number.isInteger(limits?.max) ? limits.max : null;
	if (min !== null && min < own.min) {
		return `“${group}” needs at least ${own.min} ${entriesWord(own.min)} on its card, so a form cannot take fewer. Set the form’s fewest to ${own.min} or more.`;
	}
	if (max !== null && max > own.max) {
		return `“${group}” holds at most ${own.max} ${entriesWord(own.max)} on its card, so a form cannot take more. Set the form’s most to ${own.max} or fewer.`;
	}
	if (max !== null && max < 1) return `The most entries for “${group}” must be a whole number of 1 or more.`;
	const lo = min !== null ? min : own.min;
	const hi = max !== null ? max : own.max;
	if (lo > hi) return `The fewest entries for “${group}” (${lo}) is more than the most (${hi}).`;
	return null;
}

/**
 * The `entryLimits` a form sends: each asked group's limits with a blank (or
 * the group's own number) left out, and a group with none left out.
 */
export function entryLimitsForSave(limits = {}, groupNames = []) {
	const out = {};
	(groupNames || []).forEach((name) => {
		const one = limits?.[name];
		if (!one) return;
		const kept = {};
		if (Number.isInteger(one.min)) kept.min = one.min;
		if (Number.isInteger(one.max)) kept.max = one.max;
		if (Object.keys(kept).length) out[name] = kept;
	});
	return out;
}

/**
 * Why this CARD cannot take anonymous submissions at all, or null
 * (cardRefusal, plus the one refusal the create would reach later: a
 * required element that no form could either ask for or fill in).
 *
 *   chain     the card and its ancestors, root-first (utils/cards cardChain)
 *   elements  its resolved elements (getElementsForCard)
 *
 * Answers `{ code, message }` — the code is the API's, so a test or a
 * caller can tell the reasons apart without reading prose.
 */
export function cardIntakeRefusal(chain = [], elements = [], { attachmentsOn = true } = {}) {
	// The most specific explicit record setting wins, exactly as the API
	// reads it (utils/recordPolicy has no `attest`, so the raw setting is
	// read here); attestation needs a session and a second factor.
	const specific = [...(chain || [])].reverse().find(card => card?.options?.record !== undefined && card?.options?.record !== null);
	const record = specific?.options?.record;
	if (record && record !== false && record.attest && record.attest !== true) {
		return {
			code: 'intake_attest_card',
			message: 'This card requires a signed save, which a visitor without an account cannot give.',
		};
	}
	// A card that NUMBERS its items takes forms like any other (KPR-784; the
	// API dropped `intake_sequence_card`): each submission is numbered when it
	// arrives. Its numbered element is still never a question
	// (intakeBlockReason).
	// A file element is never stuck: with attachments on the form asks for it,
	// and with them off it is never required (the vendored `required` rule).
	// Stuck: neither asked nor filled in. (A repeating group, KPR-689, is never
	// filled in but is asked — so only one that cannot be asked is stuck.)
	const stuck = (elements || []).find(el => mustBeCovered(el) && !isFileElement(el)
		&& !el?.options?.drivenFrom && !isSequenceElement(el) && prefillBlockReason(el)
		&& intakeBlockReason(el, { attachmentsOn }));
	if (stuck) {
		return {
			code: 'intake_required_uncovered',
			message: `${labelOf(stuck)} can be required on this card, and a form can neither ask a visitor for it nor fill it in for them.`,
		};
	}
	return null;
}

export const labelOf = element => element?.label?.singular || element?.name || 'This field';

// ------------------------------------------------------- private questions
//
// KPR-389 (keepr-api utils/intakePrivate.js): a link may ask some questions
// PRIVATELY — `privateFields`, never also in `fields` — and their answers
// never reach the record: they are kept apart for the people who manage the
// collection. A published community collection is the case it was built for:
// a reporter's name and phone number for the moderators, never for the
// record everyone reads.

/** name -> the elements whose `requiredWhen` names it (requiredWhenReferrers). */
function requiredWhenReferrers(elements = []) {
	const out = new Map();
	(elements || []).forEach((el) => {
		const clauses = Array.isArray(el?.options?.requiredWhen) ? el.options.requiredWhen : [];
		clauses.forEach((clause) => {
			const name = typeof clause?.element === 'string' ? clause.element : null;
			if (!name) return;
			if (!out.has(name)) out.set(name, []);
			if (!out.get(name).includes(el.name)) out.get(name).push(el.name);
		});
	});
	return out;
}

/**
 * Why this element can never be asked PRIVATELY, or null when it may — on
 * top of the rule every question obeys (intakeBlockReason). The API's own
 * words (privateRefusal), so the switch's reason and a refusal agree.
 */
export function privateBlockReason(element, elements = []) {
	if (!element) return 'This element is not on the card.';
	if (isFileElement(element)) {
		return 'A file is attached to the record, and everyone who reads the record can open it — it cannot be a private answer.';
	}
	// KPR-689 (keepr-api privateQuestions privateRefusal, word for word).
	if (isRepeatingGroup(element)) {
		return 'A repeating group’s entries are kept on the record, so it cannot be a private question. Ask it publicly instead.';
	}
	if (mustBeCovered(element)) {
		return 'This field is required on this card (always, or when a condition holds), and a private answer is never stored on the record.';
	}
	const by = requiredWhenReferrers(elements).get(element.name);
	if (by && by.length) {
		const byEl = (elements || []).find(el => el?.name === by[0]);
		return `Whether “${labelOf(byEl || { name: by[0] })}” is required depends on this answer, and a private answer is never stored on the record.`;
	}
	return null;
}

/**
 * May this reader see what a visitor sent privately with a submission in
 * `collection`? Their OWN role must be manage or owner — the API answers from
 * the caller's own grants and gives an admin no way in (403 otherwise), so
 * the item page does not even ask.
 */
export function canReadPrivateAnswers(collection) {
	const role = collection?.myAccess?.role;
	return role === 'manage' || role === 'owner' || collection?.myAccess?.isOwner === true;
}

/**
 * The elements a form with these fields and this prefill leaves unanswered
 * although they are, or can become, required — the API's
 * intake_required_uncovered, said before the request. An element with a
 * `requiredWhen` counts whether or not its clauses hold today: the visitor's
 * own answers may make them hold, and they cannot answer a field they were
 * never shown.
 */
export function uncoveredRequired(elements = [], fields = [], prefill = {}, { attachmentsOn = true } = {}) {
	const covered = new Set([...(fields || []), ...Object.keys(prefill || {})]);
	return (elements || []).filter(el => {
		if (!el?.name) return false;
		if (el.options?.drivenFrom || isSequenceElement(el)) return false;
		// Attachments off: a file element is never required, so needs no cover.
		if (!attachmentsOn && isFileElement(el)) return false;
		if (!mustBeCovered(el)) return false;
		return !covered.has(el.name);
	});
}

/**
 * What an API refusal on the MANAGE side means, in words a manager can act
 * on. The API's own message is already a sentence; the codes below get a
 * better one because this form knows which control caused them.
 */
export function describeIntakeError(error, elementsByName = {}) {
	const data = error?.response?.data || {};
	const label = data.element ? labelOf(elementsByName[data.element] || { name: data.element }) : null;
	switch (data.code) {
		case 'intake_not_enabled':
			return 'Intake forms are switched off for this collection. Turn on Intake forms above, then try again.';
		case 'intake_collection_archived':
			return 'An archived collection cannot take submissions.';
		case 'intake_fields_required':
			return 'Choose at least one field for the form to ask for.';
		case 'intake_unknown_field':
			return `${label || 'A chosen field'} is no longer on this card. It is listed under “No longer on the card” — remove it, then save again.`;
		case 'unknown_key':
			if (typeof data.path === 'string' && data.path.startsWith('prefill.')) {
				return `${data.path.slice('prefill.'.length)} is no longer on this card. It is listed under “No longer on the card” — remove it, then save again.`;
			}
			return data.message || 'The intake link could not be saved.';
		case 'intake_required_uncovered':
			// A repeating group (KPR-689) is never filled in: only asking it covers it.
			if (isRepeatingGroup(elementsByName[data.element])) return `${label} needs at least one entry on its card, and only a visitor can add one, so include it in the form.`;
			return `${label || 'A field'} can become required, so include it in the form or fill it in for the visitor.`;
		case 'intake_prefill_conflict':
			return `${label || 'A field'} is both asked for and filled in. Choose one.`;
		case 'intake_prefill_lookup':
			return 'A record you filled in must be a shared, accepted record of this collection.';
		case 'intake_prefill_not_allowed':
			return data.message || `${label || 'That field'} cannot be filled in for the visitor.`;
		case 'intake_uploads_invalid':
			return data.message || 'The upload settings are out of range.';
		case 'intake_private_conflict':
			return `${label || 'A field'} is asked both publicly and privately. Choose one.`;
		case 'intake_private_not_allowed':
			return `${label || 'A field'} cannot be asked privately. ${data.message || ''}`.trim();
		// KPR-803: only a question the form asks privately can be required by
		// the form (a public one is required by its card, or not at all).
		case 'intake_required_not_private':
			return `${label || 'A field'} can be required by this form only while it is asked privately. Switch Private on, or stop requiring it.`;
		// KPR-459 (element rules on): asking a field privately gives it a
		// "managers only" element rule — refused when the field already has
		// rules that say something else, and when a setup would print it.
		case 'intake_private_conflict_grant':
			return `${label || 'A field'} already has access rules that let people other than this collection's managers see or change it, so it cannot be asked privately. Make it managers-only in its Access on the card, or ask it publicly.`;
		case 'element_rule_conflicts': {
			const names = (Array.isArray(data.conflicts) ? data.conflicts : []).map(c => c?.name).filter(Boolean);
			const what = names.length ? ` (${names.slice(0, 3).join(', ')}${names.length > 3 ? ', …' : ''})` : '';
			return `A private question would be hidden from people something on this card shows it to${what}. Change that first, then save the form again.`;
		}
		case 'intake_uploads_without_file':
			return 'Upload settings need a file field on the form. Tick one, or leave the upload settings alone.';
		// KPR-519 (refusal R10): the form is held to what an element's rules
		// let PUBLIC FORM VISITORS do with it.
		case 'intake_shown_not_see_only':
			return `${label || 'A field'} can be shown only while it is see only for public form visitors. Change that in its Access on the card, or stop showing it.`;
		case 'intake_field_restricted':
			if (data.source) {
				// A "required when" or a date limit reads an element hidden from
				// the public: answering would tell them its value.
				const source = labelOf(elementsByName[data.source] || { name: data.source });
				return `${label || 'A field'} depends on ${source}, which is hidden from public form visitors, so the form cannot ask for it. Stop asking for it, or change the element rules.`;
			}
			return data.level === 'see'
				? `${label || 'A field'} is see only for public form visitors: show it read-only instead of asking for it.`
				: `${label || 'A field'} is hidden from public form visitors by its element rules, so the form cannot ask for it.`;
		// KPR-689: a repeating group's fewest or most entries out of the group's
		// own — the API's sentence names the group's limit.
		case 'intake_entry_limits':
			return data.message || `The entries ${label || 'a group'} takes are out of its card's range.`;
		case 'intake_shown_conflict':
			return `${label || 'A field'} is both shown and asked for or filled in. Choose one.`;
		case 'intake_shown_bound': {
			const source = labelOf(elementsByName[data.source] || { name: data.source || 'another field' });
			return `${label || 'A field'} has a date limit tied to ${source}, which is filled in for the visitor, so it cannot be shown. Stop filling in ${source}, or stop showing ${label || 'it'}.`;
		}
		case 'link_revoked':
			return 'This link has been revoked, so it can no longer be changed.';
		case 'link_not_editable':
			return 'Only an intake link can be edited. Make a new link instead.';
		default:
			return data.message || 'The intake link could not be saved.';
	}
}

/**
 * What a refusal on the PUBLIC submit means to the visitor. 404 is not here:
 * every dead-link condition is the same flat 404, and the page swaps to the
 * invalid-link screen rather than explaining something it cannot know.
 */
export function describeSubmitError(error) {
	const status = error?.response?.status;
	const data = error?.response?.data || {};
	// A permanent public form's own two (KPR-512): its window is full, or it
	// was paused while the visitor typed. Nothing was written and the token is
	// unspent, so the answers stay on the page for another try.
	if (status === 429 && data.code === 'form_limit_reached') {
		const wait = retryAfterSeconds(error);
		return `This form has taken all the submissions it can for now. Your answers are still here — try again ${wait ? `in ${formatWait(wait)}` : 'later'}.`;
	}
	if (status === 409 && data.code === 'form_paused') {
		return 'This form is paused, so it cannot take your answers right now. They are still here — try again later.';
	}
	if (status === 429) return describeSubmitRateLimit(error);
	switch (data.code) {
		case 'submission_token_invalid':
			return 'This form has been open too long. Reload the page and send it again — your answers will need re-entering.';
		case 'challenge_failed':
			return 'The check that you are not a bot did not pass. Wait for it to finish, then send again.';
		case 'challenge_unavailable':
			return 'The bot check is not answering right now. Try again in a few minutes.';
		case 'form_out_of_date':
			return 'This form needs updating by its owner. Try again later.';
		case 'file_not_found':
			return 'One of your files is no longer on the form. Remove it, add it again, then send.';
		case 'upload_limit':
			return `This form takes at most ${data.maxFiles || 'a few'} files and ${data.maxSubmissionMb || 'a few'} MB at once. Remove one, then send.`;
		case 'required':
		case 'invalid_elements':
			return (data.message || 'Some answers need a look before this can be sent.').replace(/^elements\.[^:]+:\s*/, '');
		default:
			break;
	}
	if (status === 413) return 'That is more than this form can take in one go. Shorten your answers and send again.';
	return data.message || 'That did not go through. Check your answers and try again.';
}

/**
 * A 429 on the public submit, said as which budget ran out and when to come
 * back. Two budgets can refuse (SCHEMA § Intake links, Rate limit): the
 * LINK's day, which every visitor shares and which only tomorrow resets, and
 * this NETWORK's hour or day, which Retry-After times.
 */
export function describeSubmitRateLimit(error) {
	const headers = error?.response?.headers || {};
	const limit = Number(headers['ratelimit-limit']);
	if (limit === INTAKE_LINK_DAILY_LIMIT) {
		return 'This form has taken all the submissions it can today — try again tomorrow.';
	}
	const wait = retryAfterSeconds(error);
	return wait
		? `Too many submissions from this network. Try again in ${formatWait(wait)}.`
		: 'Too many submissions from this network. Try again later.';
}

// ------------------------------------------------------------------ uploads
//
// A public form's photos (KPR-388; keepr-api utils/intakeUploads.js). What a
// manager may let a form take, what the visitor's page refuses before a byte
// moves, and the words for every refusal the upload route sends.

/** What a form may take (`uploads.types`). */
export const UPLOAD_TYPES = ['photo', 'pdf'];
/** The API's defaults and ceilings (DEFAULTS, PUBLIC_MAX_FILE_MB, MAX_SUBMISSION_MB, MAX_FILES). */
export const DEFAULT_UPLOADS = Object.freeze({ types: ['photo'], maxFileMb: 10, maxSubmissionMb: 25, maxFiles: 5 });
export const MAX_UPLOAD_FILE_MB = 10;
export const MAX_UPLOAD_SUBMISSION_MB = 50;
export const MAX_UPLOAD_FILES = 20;

// The photo kinds a public form takes — by the bytes on the server; by the
// name and the browser's type here, which is enough to stop a doomed upload.
// GIF, AVIF, TIFF and SVG are refused by the API.
const PHOTO_MIMES = ['image/jpeg', 'image/png', 'image/webp', 'image/heic', 'image/heif'];
const PHOTO_EXTENSIONS = ['jpg', 'jpeg', 'jpe', 'png', 'webp', 'heic', 'heif'];

const extensionOf = name => {
	const match = /\.([A-Za-z0-9]{1,8})$/.exec(`${name || ''}`);
	return match ? match[1].toLowerCase() : '';
};

/** 'photo', 'pdf' or null: what this picked file would be. */
export function publicFileKind(file) {
	const mime = `${file?.type || ''}`.toLowerCase();
	const ext = extensionOf(file?.name);
	if (PHOTO_MIMES.includes(mime)) return 'photo';
	if (mime === 'application/pdf') return 'pdf';
	// Another image type the browser named (GIF, AVIF, TIFF, SVG): not taken.
	if (mime.startsWith('image/')) return null;
	// No type, or a generic one (a HEIC on Windows reads as octet-stream): the name.
	if (PHOTO_EXTENSIONS.includes(ext)) return 'photo';
	if (ext === 'pdf') return 'pdf';
	return null;
}

/**
 * The picker's `accept` for what an element takes. Never `capture`: a phone
 * then offers the camera AND the library. `.heic` / `.heif` are named because
 * some desktop browsers do not count them as image/*.
 */
export function uploadAccept(types = DEFAULT_UPLOADS.types) {
	const parts = [];
	if (types.includes('photo')) parts.push('image/jpeg', 'image/png', 'image/webp', 'image/heic', 'image/heif', '.jpg', '.jpeg', '.png', '.webp', '.heic', '.heif');
	if (types.includes('pdf')) parts.push('application/pdf', '.pdf');
	return parts.join(',');
}

/** What an element takes, in words: "photos", "photos or PDFs", "PDFs". */
export function typesInWords(types = DEFAULT_UPLOADS.types) {
	if (types.includes('photo') && types.includes('pdf')) return 'photos or PDFs';
	return types.includes('pdf') ? 'PDFs' : 'photos';
}

const mbOf = bytes => Math.max(0.1, Math.round((bytes / (1024 * 1024)) * 10) / 10);

/**
 * Why the page will not send this file, or null — the API's own refusals,
 * said before the bytes move: the kind, the size, and the submission's caps
 * (`held`: the files already on the form, `{ count, bytes }`).
 */
export function precheckPublicFile(file, upload = {}, caps = {}, held = { count: 0, bytes: 0 }) {
	const types = Array.isArray(upload.types) && upload.types.length ? upload.types : DEFAULT_UPLOADS.types;
	const maxFileMb = upload.maxFileMb || DEFAULT_UPLOADS.maxFileMb;
	const kind = publicFileKind(file);
	if (!kind || !types.includes(kind)) {
		if (types.includes('photo') && types.includes('pdf')) return 'This takes a photo (JPEG, PNG, WebP or HEIC) or a PDF.';
		return types.includes('pdf') ? 'This takes a PDF.' : 'This takes a photo (JPEG, PNG, WebP or HEIC).';
	}
	const size = Number(file?.size) || 0;
	if (size === 0) return 'That file is empty.';
	if (size > maxFileMb * 1024 * 1024) return `That file is ${mbOf(size)} MB; this form takes files up to ${maxFileMb} MB.`;
	if (caps.maxFiles && held.count >= caps.maxFiles) return `This form takes at most ${caps.maxFiles} file${caps.maxFiles === 1 ? '' : 's'}. Remove one first.`;
	if (caps.maxSubmissionMb && held.bytes + size > caps.maxSubmissionMb * 1024 * 1024) return `Your files would pass ${caps.maxSubmissionMb} MB, the most this form takes at once. Remove one first.`;
	return null;
}

/** An upload the API refused, in words for the visitor. */
export function describeUploadError(error) {
	const status = error?.response?.status;
	const data = error?.response?.data || {};
	switch (data.code) {
		case 'file_too_large':
			return data.message || `That file is too large. This form takes files up to ${data.limitMb || MAX_UPLOAD_FILE_MB} MB.`;
		case 'file_wrong_kind':
			return data.message || 'That is not a kind of file this form takes.';
		case 'upload_unreadable':
			return 'That photo could not be read. Try another, or take it again.';
		case 'upload_limit':
			return `This form takes at most ${data.maxFiles || 'a few'} files and ${data.maxSubmissionMb || 'a few'} MB at once. Remove one first.`;
		case 'upload_allowance_reached':
			return 'This form has taken all the files it can today. You can still send it without them, or try again tomorrow.';
		case 'upload_busy':
			return 'Too many photos are being sent right now. Try again in a moment.';
		case 'upload_timeout':
		case 'upload_aborted':
			return 'The upload did not finish. Check the connection and try again.';
		case 'submission_token_invalid':
			return 'This form had been open a while. It was refreshed — add the file again.';
		case 'upload_element_unknown':
			return 'This form no longer takes a file there. Reload the page.';
		// A permanent public form checks for a person on every upload (KPR-512).
		case 'challenge_failed':
			return 'The check that you are not a bot did not pass for this file. Try it again.';
		case 'challenge_unavailable':
			return 'The bot check is not answering right now. Try this file again in a few minutes.';
		case 'form_paused':
			return 'This form is paused, so it cannot take files right now. Try again later.';
		default:
			break;
	}
	if (status === 429) return 'Too many uploads from this network. Try again later.';
	if (status === 413) return `That file is too large. This form takes files up to ${MAX_UPLOAD_FILE_MB} MB.`;
	if (!status) return 'The upload did not reach keepr. Check the connection and try again.';
	return data.message || 'That file could not be sent.';
}

/** Seconds a 503 / 429 asked the page to wait (Retry-After), capped; null when none. */
export function uploadRetryAfter(error, cap = 10) {
	const ms = retryAfterMs(error);
	return ms !== null && ms > 0 ? Math.min(ms / 1000, cap) : null;
}
